import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	WidgetStatus,
	portfolioWidgetMissingDataReason,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { type ContextContent, withContext } from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PortfolioContext } from "../../contexts/portfolio";
import type { InvestmentSummaryWithRequiredUuid } from "$root/pages/PortfolioDetails/common";
import { DropdownMenu, IconButton } from "@mdotm/shadcn/mdotm/components";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import type { PerformanceSeriesData, VisibleSeries } from "./common";
import { buildPerformanceMetrics, exportPortfolioPerformance, toExportDateRange } from "./export";
import { PerformancePanel, type PerformancePanelHandle, type PerformancePanelRow } from "./PerformancePanel";
import { useUserStorageJson } from "$root/functional-areas/user/useUserStorage";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import z from "zod";

const zVisibleSeries = z.record(z.boolean());

type PerformanceSeries = {
	uuid: string;
	name: string;
	series: PerformanceSeriesData;
	benchmark?: {
		uuid: string;
		name: string;
		series: Array<[timestampSec: number, value: number]>;
	};
};

type Props = {
	performanceSeries: Array<PerformanceSeries>;
	importedPerformances?: InvestmentSummaryWithRequiredUuid["importedPerformances"];
	portfolioUuid: string;
	benchmarkId: string | null;
	/** Validated as-of date (`YYYY-MM-DD`) when the page is pinned to a past snapshot. */
	asOf?: string;
};

function PortfolioPerformanceInner(props: Props) {
	const { performanceSeries } = props;
	const { t } = useTranslation();
	const panelRef = useRef<PerformancePanelHandle | null>(null);
	const [isFetching, setIsFetching] = useState(false);

	// Portfolio-details persists both concerns to remote user storage (unchanged behavior).
	const [visibleSeries, setVisibleSeries] = useUserStorageJson<VisibleSeries>(
		"portfolio-performance-series-visibility",
		{},
		{ fromPrimitives: (val) => zVisibleSeries.parse(val) },
	);
	const columnPersistence = agExtra.usePersistColState("portfolio-performance");

	const rows = useMemo<PerformancePanelRow[]>(
		() =>
			performanceSeries.flatMap((item) =>
				!item.benchmark
					? [{ uuid: item.uuid, name: item.name, series: item.series }]
					: [
							{ uuid: item.uuid, name: item.name, series: item.series },
							{ uuid: item.benchmark.uuid, name: item.benchmark.name, series: item.benchmark.series },
						],
			),
		[performanceSeries],
	);

	useWidgetOptions(
		() => ({
			title: t("PORTFOLIO_PERFORMANCE.TITLE"),
			tooltip: t("PORTFOLIO_PERFORMANCE.TOOLTIP"),
			maxHeight: 512 + 60,
			isLoading: isFetching,
			actionHeader: function ActionHeader() {
				return (
					<>
						{props.importedPerformances?.lastAuditedDate && (
							<div className="flex flex-row items-center gap-2 mr-4">
								<ColoredRectangle variant="vertical" color={themeCSSVars.palette_S200} />
								Last audited performance
							</div>
						)}

						<DropdownMenu
							Trigger={WidgetDownloadTrigger}
							actions={[
								{
									icon: "Dowload",
									onClickAsync: async () => {
										// Snapshot the current chart zoom + visible metrics so the export mirrors what the user sees.
										const snapshot = panelRef.current?.getExportSnapshot();
										await exportPortfolioPerformance({
											uuid: props.portfolioUuid,
											benchmarkId: props.benchmarkId ?? undefined,
											asOfDate: props.asOf,
											dateRange: snapshot && toExportDateRange(snapshot.dateRange),
											investmentMetrics: buildPerformanceMetrics(snapshot?.computedMetrics, props.portfolioUuid),
											benchmarkMetrics: props.benchmarkId
												? buildPerformanceMetrics(snapshot?.computedMetrics, props.benchmarkId)
												: [],
										});
									},
									label: t("PORTFOLIO_PERFORMANCE.DOWNLOAD_TITLE"),
								},
							]}
						/>
						<IconButton
							applyNegativeMargins
							onClick={() => panelRef.current?.openSettings()}
							icon="Settings"
							color={themeCSSVars.palette_N500}
							size={20}
						/>
					</>
				);
			},
		}),
		[props.benchmarkId, props.portfolioUuid, props.importedPerformances?.lastAuditedDate, props.asOf, t, isFetching],
	);

	return (
		<PerformancePanel
			ref={panelRef}
			rows={rows}
			seriesVisibility={{ value: visibleSeries ?? {}, onChange: setVisibleSeries }}
			columnPersistence={columnPersistence}
			queryKeyPrefix="portfolio-performance"
			gridTitle="Portfolio performance Settings"
			gridSubtitle="Manage metrics display options"
			importedPerformances={props.importedPerformances}
			onFetchingChange={setIsFetching}
		/>
	);
}

const PortfolioPerformance = ({
	portfolio,
	selectedBenchmark: benchmarkId,
	asOf,
	asOfSnapshot,
}: ContextContent<typeof PortfolioContext>) => {
	const { t } = useTranslation();
	const queryRaw = useQuery({
		...portfolioRepository.performance.getQueryConfig(portfolio?.uuid ?? "", { benchmarkId, asOf }),
		enabled: Boolean(portfolio) && Boolean(benchmarkId) && isAsOfReadable(asOf, asOfSnapshot),
	});

	const performanceSeriesQuery = useMappedQuery(
		(data): PortfolioWidgetData<Array<PerformanceSeries> | undefined> => {
			if (!data?.current || !data.benchmark) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(portfolio, "PortfolioPerformance"),
				};
			}

			// A payload whose series carries no point has nothing to plot.
			if (data.current.length === 0) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.EMPTY,
				};
			}

			return {
				data: [
					{
						uuid: portfolio.uuid ?? "",
						name: "Portfolio",
						series: data.current as Array<[timestampSec: number, value: number]>,
						benchmark:
							!data.benchmark || !benchmarkId
								? undefined
								: {
										uuid: benchmarkId,
										name: "Portfolio Benchmark",
										series: data.benchmark as Array<[timestampSec: number, value: number]>,
									},
					},
				],
				widgetStatus: WidgetStatus.READY,
			};
		},
		[portfolio, benchmarkId],
		queryRaw,
	);

	useWidgetOptions(
		() => ({
			title: t("PORTFOLIO_PERFORMANCE.TITLE"),
		}),
		[t],
	);

	return (
		<PortfolioQueryWidgetBase query={performanceSeriesQuery}>
			{(performanceSeries) => (
				<PortfolioPerformanceInner
					performanceSeries={performanceSeries}
					importedPerformances={portfolio.importedPerformances}
					portfolioUuid={portfolio.uuid}
					benchmarkId={benchmarkId}
					asOf={asOf}
				/>
			)}
		</PortfolioQueryWidgetBase>
	);
};

export default withContext(PortfolioContext)(PortfolioPerformance);
